import { useState, useEffect, useCallback } from 'react';
import {
  fetchProperties, fetchProperty, createProperty, updateProperty, deleteProperty,
  markPropertyDeeded, markPropertyReleased, revertPropertyRelease,
  createPropertyPayment, deletePropertyPayment, updatePropertyPayment, markPaymentReceived, revertPaymentToProjected,
  fetchPaymentAbonos, createPaymentAbono, updatePaymentAbono, deletePaymentAbono,
  fetchProjectConfig,
} from '@/lib/api';
import { formatMXN, formatMXNCompact, formatDate } from '@/lib/formatters';
import { useAuth } from '@/lib/AuthContext';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { KPICard } from '@/components/common/KPICard';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus, Search, Trash2, Home, DollarSign, Pencil, Check, X as XIcon,
  Wallet, TrendingUp, AlertTriangle, Calendar, CheckCircle2, Clock, Ban,
} from 'lucide-react';

const STATUSES = ['Disponible', 'Apartada', 'Vendida'];
const PAY_TYPES = [
  { value: 'anticipo', label: 'Anticipo' },
  { value: 'pago_intermedio', label: 'Pago Intermedio' },
  { value: 'finiquito', label: 'Finiquito' },
];
const statusColors = {
  Disponible: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Apartada: 'bg-amber-50 text-amber-700 border-amber-200',
  Vendida: 'bg-slate-100 text-slate-700 border-slate-200',
};
const payTypeConfig = {
  anticipo:          { badge: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500', label: 'Anticipo' },
  pago_intermedio:   { badge: 'bg-violet-50 text-violet-700 border-violet-200', dot: 'bg-violet-500', label: 'Intermedio' },
  intermedio:        { badge: 'bg-violet-50 text-violet-700 border-violet-200', dot: 'bg-violet-500', label: 'Intermedio' },
  finiquito:         { badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', label: 'Finiquito' },
};
const payStatusConfig = {
  projected: { badge: 'bg-slate-50 text-slate-600 border-slate-200', icon: Clock, label: 'Programado' },
  received:  { badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2, label: 'Recibido' },
  overdue:   { badge: 'bg-red-50 text-red-600 border-red-200', icon: AlertTriangle, label: 'Vencido' },
  cancelled: { badge: 'bg-slate-50 text-slate-400 border-slate-200', icon: Ban, label: 'Cancelado' },
};
const emptyProp = { name: '', lot: '', block: '', modelo: 'Casa Ámbar', sale_price: '', deed_date: '', delivery_date: '', release_amount: '', status: 'Disponible', client_email: '', client_phone: '' };
const emptyPay = { payment_type: 'anticipo', scheduled_date: '', scheduled_amount: '', aplica_a_credito: false, fuera_de_flujo: false };

/* ══════════════════════════════════════════════════════ */
export default function Viviendas() {
  const [props, setProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [modelos, setModelos] = useState(['Casa Ámbar', 'Casa Jade', 'Casa Ópalo']);

  const load = useCallback(async () => {
    try {
      const [propsData, configData] = await Promise.all([fetchProperties(), fetchProjectConfig()]);
      setProps(propsData);
      if (configData.modelos_vivienda?.length) setModelos(configData.modelos_vivienda);
    } catch { toast.error('Error al cargar propiedades'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = props.filter(p => {
    const q = search.toLowerCase();
    const ms = !search || (p.name||'').toLowerCase().includes(q) || (p.modelo||'').toLowerCase().includes(q) || `${p.lot} ${p.block}`.toLowerCase().includes(q);
    const st = filterStatus === 'all' || p.status === filterStatus;
    return ms && st;
  });

  const totalProps = props.length;
  const totalVendidas = props.filter(p => p.status === 'Vendida').length;
  const totalRecibido = props.reduce((s, p) => s + (p.total_paid || 0), 0);
  const totalSaldo = props.reduce((s, p) => s + (p.saldo_pendiente || 0), 0);

  const handleDelete = async (id) => { try { await deleteProperty(id); setProps(ps => ps.filter(p => p.id !== id)); toast.success('Eliminada'); } catch { toast.error('Error'); } };
  const handleCreated = (c) => { setProps(ps => [...ps, c]); setCreateOpen(false); };
  const handleUpdated = (u) => { setProps(ps => ps.map(p => p.id === u.id ? u : p)); setEditTarget(null); };
  const openDetail = async (p) => { try { setDetailTarget(await fetchProperty(p.id)); } catch { toast.error('Error al cargar detalle'); } };

  if (loading) return (<div className="space-y-6"><Skeleton className="h-10 w-48" /><div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div><Skeleton className="h-[400px] rounded-xl" /></div>);

  return (
    <div className="space-y-4" data-testid="viviendas-page">
        <PageHeader title="Viviendas" subtitle={`${totalProps} propiedades en el desarrollo`}>
          <Button onClick={() => setCreateOpen(true)} data-testid="table-toolbar-add-button"><Plus className="h-4 w-4 mr-1.5" /> Nueva Vivienda</Button>
        </PageHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <KPICard label="Total Viviendas" value={`${totalVendidas} vendidas de ${totalProps}`} icon={Home} compact testId="kpi-viviendas-total" />
          <KPICard label="Total Recibido" value={formatMXNCompact(totalRecibido)} icon={DollarSign} compact testId="kpi-viviendas-recibido" />
          <KPICard label="Saldo Pendiente" value={formatMXNCompact(totalSaldo)} icon={Wallet} compact testId="kpi-viviendas-saldo" />
          <KPICard label="Avance Cobro" value={`${totalRecibido + totalSaldo > 0 ? ((totalRecibido / (totalRecibido + totalSaldo)) * 100).toFixed(1) : 0}%`} icon={TrendingUp} compact testId="kpi-viviendas-avance" />
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-1 items-center gap-2">
            <div className="relative max-w-xs w-full sm:w-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Buscar comprador, lote o modelo..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" data-testid="table-toolbar-search-input" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[160px] h-9" data-testid="table-toolbar-filter-status"><SelectValue placeholder="Estatus" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Todos</SelectItem>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <p className="text-xs text-slate-400">{filtered.length} resultados</p>
        </div>

      {/* ── Table ── */}
      {filtered.length === 0 ? <EmptyState title="Sin propiedades" icon={Home} /> : (
        <Card className="rounded-xl border bg-white overflow-clip">
            <Table data-testid="viviendas-table">
              <TableHeader className="sticky top-0 z-20">
                <TableRow className="bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
                  {/* 1.Comprador 2.Lote 3.Mz 4.Modelo 5.Escrituracion 6.Precio 7.Recibido 8.Saldo 9.Liberacion 10.Estatus */}
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Comprador</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Lote</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Mz</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Modelo</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Escrituración</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right">Precio</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right">Recibido</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right">Saldo</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3 text-right">Liberación</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-3">Estatus</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p, idx) => {
                  const pct = p.sale_price > 0 ? (p.total_paid / p.sale_price) * 100 : 0;
                  return (
                    <TableRow key={p.id} className="table-row-hover group" data-testid={`viviendas-row-${idx}`}>
                      <TableCell className="py-2.5 px-3 text-[13px]">
                        {p.name ? (
                          <button onClick={() => openDetail(p)} className="text-left hover:text-[#F05A28] transition-colors font-medium" data-testid={`viviendas-detail-${idx}`}>{p.name}</button>
                        ) : (
                          <span className="text-slate-300 italic text-[12px]">Sin comprador</span>
                        )}
                      </TableCell>
                      <TableCell className="py-2.5 px-3 text-[13px] font-medium tabular-nums">{p.lot}</TableCell>
                      <TableCell className="py-2.5 px-3 text-[13px] font-semibold">{p.block}</TableCell>
                      <TableCell className="py-2.5 px-3 text-[13px] text-slate-600">{p.modelo || '—'}</TableCell>
                      <TableCell className="py-2.5 px-3 text-[13px] text-slate-500">{p.deed_date ? formatDate(p.deed_date) : <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="py-2.5 px-3 text-right tabular-nums text-[13px] font-medium">{formatMXN(p.sale_price)}</TableCell>
                      <TableCell className="py-2.5 px-3 text-right tabular-nums text-[13px]">
                        <div className="flex flex-col items-end gap-0.5">
                          <span className="font-medium text-emerald-700">{formatMXN(p.total_paid)}</span>
                          <div className="w-16 h-1 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${Math.min(pct, 100)}%` }} />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className={`py-2.5 px-3 text-right tabular-nums text-[13px] font-medium ${p.saldo_pendiente > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{formatMXN(p.saldo_pendiente)}</TableCell>
                      <TableCell className="py-2.5 px-3 text-right tabular-nums text-[13px]">{p.release_amount ? formatMXN(p.release_amount) : <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="py-2.5 px-3"><Badge variant="outline" className={`${statusColors[p.status] || ''} text-xs font-medium`}>{p.status}</Badge></TableCell>
                      <TableCell className="py-2.5 px-3">
                        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-[#F05A28]" onClick={() => openDetail(p)} title="Pagos"><DollarSign className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-slate-700" onClick={() => setEditTarget(p)} data-testid={`viviendas-edit-${idx}`}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500" onClick={() => handleDelete(p.id)} data-testid={`viviendas-delete-${idx}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Totals row */}
                {filtered.length > 0 && (() => {
                  const totPrecio = filtered.reduce((s, p) => s + (p.sale_price || 0), 0);
                  const totRecibido = filtered.reduce((s, p) => s + (p.total_paid || 0), 0);
                  const totSaldo = filtered.reduce((s, p) => s + (p.saldo_pendiente || 0), 0);
                  const totLiberacion = filtered.reduce((s, p) => s + (p.release_amount || 0), 0);
                  return (
                    <TableRow className="bg-slate-50/80 border-t-2 border-slate-200" data-testid="viviendas-totals-row">
                      <TableCell className="py-2.5 px-3 text-[11px] font-bold text-slate-600 uppercase" colSpan={5}>Totales ({filtered.length} viviendas)</TableCell>
                      <TableCell className="py-2.5 px-3 text-right tabular-nums text-[13px] font-bold text-slate-800">{formatMXN(totPrecio)}</TableCell>
                      <TableCell className="py-2.5 px-3 text-right tabular-nums text-[13px] font-bold text-emerald-700">{formatMXN(totRecibido)}</TableCell>
                      <TableCell className="py-2.5 px-3 text-right tabular-nums text-[13px] font-bold text-amber-600">{formatMXN(totSaldo)}</TableCell>
                      <TableCell className="py-2.5 px-3 text-right tabular-nums text-[13px] font-bold text-slate-700">{formatMXN(totLiberacion)}</TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  );
                })()}
              </TableBody>
            </Table>
        </Card>
      )}

      <CreatePropertyModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={handleCreated} modelos={modelos} />
      {editTarget && <EditPropertyModal property={editTarget} onClose={() => setEditTarget(null)} onUpdated={handleUpdated} modelos={modelos} />}
      {detailTarget && <PropertyDetailModal property={detailTarget} onClose={() => setDetailTarget(null)} onRefresh={load} modelos={modelos} />}
    </div>
  );
}

/* ── Create Modal ── */
function CreatePropertyModal({ open, onClose, onCreated, modelos }) {
  const [form, setForm] = useState({ ...emptyProp });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleSave = async () => {
    setSaving(true);
    try {
      const d = { ...form, sale_price: parseFloat(form.sale_price) || 0, release_amount: parseFloat(form.release_amount) || 0, deed_date: form.deed_date || null, delivery_date: form.delivery_date || null, client_email: form.client_email || null, client_phone: form.client_phone || null };
      const created = await createProperty(d);
      onCreated(created); setForm({ ...emptyProp }); toast.success('Vivienda creada');
    } catch { toast.error('Error al crear'); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><div className="h-8 w-8 rounded-lg bg-[rgba(240,90,40,0.1)] flex items-center justify-center"><Home className="h-4 w-4 text-[#F05A28]" /></div>Nueva Vivienda</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Nombre del comprador</label><Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Nombre completo" data-testid="create-prop-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Email del comprador</label><Input type="email" value={form.client_email} onChange={e => set('client_email', e.target.value)} placeholder="correo@ejemplo.com" /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Teléfono del comprador</label><Input value={form.client_phone} onChange={e => set('client_phone', e.target.value)} placeholder="10 dígitos ej: 5551020707" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Lote</label><Input value={form.lot} onChange={e => set('lot', e.target.value)} placeholder="1" /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Manzana</label><Input value={form.block} onChange={e => set('block', e.target.value)} placeholder="I" /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Modelo</label>
              <Select value={form.modelo} onValueChange={v => set('modelo', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{modelos.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Precio de venta (MXN)</label><Input type="number" value={form.sale_price} onChange={e => set('sale_price', e.target.value)} placeholder="1850000" data-testid="create-prop-price" /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Monto liberación</label><Input type="number" value={form.release_amount} onChange={e => set('release_amount', e.target.value)} placeholder="1480000" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Fecha escrituración</label><Input type="date" value={form.deed_date} onChange={e => set('deed_date', e.target.value)} /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Fecha de entrega</label><Input type="date" value={form.delivery_date} onChange={e => set('delivery_date', e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Estatus</label>
              <Select value={form.status} onValueChange={v => set('status', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
        </div>
        <DialogFooter><DialogClose asChild><Button variant="outline">Cancelar</Button></DialogClose><Button onClick={handleSave} disabled={!form.sale_price || saving} data-testid="create-prop-save">{saving ? 'Guardando...' : 'Crear Vivienda'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Edit Modal ── */
function EditPropertyModal({ property, onClose, onUpdated, modelos }) {
  const [form, setForm] = useState({ name: property.name||'', lot: property.lot||'', block: property.block||'', modelo: property.modelo||'', sale_price: property.sale_price, release_amount: property.release_amount||'', deed_date: property.deed_date||'', delivery_date: property.delivery_date||'', status: property.status, client_email: property.client_email||'', client_phone: property.client_phone||'' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleSave = async () => {
    setSaving(true);
    try {
      const d = { ...form, sale_price: parseFloat(form.sale_price)||0, release_amount: parseFloat(form.release_amount)||0, deed_date: form.deed_date||null, client_email: form.client_email||null, client_phone: form.client_phone||null };
      const updated = await updateProperty(property.id, d);
      onUpdated(updated); toast.success('Vivienda actualizada');
    } catch { toast.error('Error al actualizar'); } finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center"><Pencil className="h-4 w-4 text-slate-600" /></div>Editar — Lote {property.lot} Mz {property.block}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Nombre del comprador</label><Input value={form.name} onChange={e => set('name', e.target.value)} data-testid="edit-prop-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Email del comprador</label><Input type="email" value={form.client_email} onChange={e => set('client_email', e.target.value)} placeholder="correo@ejemplo.com" /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Teléfono del comprador</label><Input value={form.client_phone} onChange={e => set('client_phone', e.target.value)} placeholder="10 dígitos ej: 5551020707" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Lote</label><Input value={form.lot} onChange={e => set('lot', e.target.value)} /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Manzana</label><Input value={form.block} onChange={e => set('block', e.target.value)} /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Modelo</label>
              <Select value={form.modelo} onValueChange={v => set('modelo', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{modelos.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Precio de venta (MXN)</label><Input type="number" value={form.sale_price} onChange={e => set('sale_price', e.target.value)} /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Monto liberación</label><Input type="number" value={form.release_amount} onChange={e => set('release_amount', e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Fecha escrituración</label><Input type="date" value={form.deed_date} onChange={e => set('deed_date', e.target.value)} data-testid="edit-prop-deed" /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Fecha de entrega</label><Input type="date" value={form.delivery_date} onChange={e => set('delivery_date', e.target.value)} data-testid="edit-prop-delivery" /></div>
            <div><label className="text-xs font-medium text-slate-600 mb-1.5 block">Estatus</label>
              <Select value={form.status} onValueChange={v => set('status', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
        </div>
        <DialogFooter><DialogClose asChild><Button variant="outline">Cancelar</Button></DialogClose><Button onClick={handleSave} disabled={!form.sale_price || saving}>{saving ? 'Guardando...' : 'Guardar Cambios'}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ── Detail + Payment Calendar Modal ── */
function PropertyDetailModal({ property: initProp, onClose, onRefresh, modelos }) {
  const [prop, setProp] = useState(initProp);
  const [busy, setBusy] = useState(false);
  const { can } = useAuth();
  const canEditPayments = can('pagos', 'edit');
  const canEditViviendas = can('viviendas', 'edit');
  const [editGeneralOpen, setEditGeneralOpen] = useState(false);
  // New payment form
  const [payForm, setPayForm] = useState({ ...emptyPay });
  const [alreadyReceived, setAlreadyReceived] = useState(false);
  const [payActual, setPayActual] = useState({ actual_date: '', actual_amount: '' });
  // Edit modal
  const [editTarget, setEditTarget] = useState(null);
  const [abonos, setAbonos] = useState([]);
  const [abonoForm, setAbonoForm] = useState({ date: '', amount: '', notes: '' });
  const [editingAbono, setEditingAbono] = useState(null);
  // Edit scheduled modal
  const [editSchedTarget, setEditSchedTarget] = useState(null);
  // Delete
  const [deleteTarget, setDeleteTarget] = useState(null);

  const reload = async () => { try { setProp(await fetchProperty(prop.id)); } catch {} };

  /* ── Create payment ── */
  const addPayment = async () => {
    setBusy(true);
    try {
      if (alreadyReceived) {
        // Create as received in one step
        const created = await createPropertyPayment({
          property_id: prop.id,
          payment_type: payForm.payment_type,
          scheduled_date: payForm.scheduled_date,
          scheduled_amount: parseFloat(payForm.scheduled_amount) || 0,
          payment_status: 'projected',
          aplica_a_credito: payForm.aplica_a_credito || false,
          fuera_de_flujo: payForm.fuera_de_flujo || false,
        });
        await markPaymentReceived(created.id, {
          actual_date: payActual.actual_date || payForm.scheduled_date,
          actual_amount: payActual.actual_amount ? parseFloat(payActual.actual_amount) : (parseFloat(payForm.scheduled_amount) || 0),
        });
        toast.success('Pago registrado como recibido');
      } else {
        await createPropertyPayment({
          property_id: prop.id,
          payment_type: payForm.payment_type,
          scheduled_date: payForm.scheduled_date,
          scheduled_amount: parseFloat(payForm.scheduled_amount) || 0,
          payment_status: 'projected',
          aplica_a_credito: payForm.aplica_a_credito || false,
          fuera_de_flujo: payForm.fuera_de_flujo || false,
        });
        toast.success('Pago programado');
      }
      setPayForm({ ...emptyPay });
      setAlreadyReceived(false);
      setPayActual({ actual_date: '', actual_amount: '' });
      await reload(); onRefresh();
    } catch { toast.error('Error al crear pago'); }
    finally { setBusy(false); }
  };

  /* ── Save edit — removed: scheduled data is read-only ── */

  const handleAddAbono = async () => {
    if (!editTarget || !abonoForm.date || !abonoForm.amount) return;
    setBusy(true);
    try {
      await createPaymentAbono({
        payment_id: editTarget.id,
        date: abonoForm.date,
        amount: parseFloat(abonoForm.amount),
        notes: abonoForm.notes || null,
      });
      setAbonoForm({ date: '', amount: '', notes: '' });
      const [newAbonos, newProp] = await Promise.all([fetchPaymentAbonos(editTarget.id), fetchProperty(prop.id)]);
      setAbonos(newAbonos);
      setProp(newProp);
      // Update editTarget with fresh data
      const freshPay = newProp.payments?.find(p => p.id === editTarget.id);
      if (freshPay) setEditTarget(freshPay);
      toast.success('Abono registrado');
      onRefresh();
    } catch { toast.error('Error al registrar abono'); }
    finally { setBusy(false); }
  };

  const handleSaveAbono = async (abonoId, updates) => {
    try {
      await updatePaymentAbono(abonoId, updates);
      const [newAbonos, newProp] = await Promise.all([fetchPaymentAbonos(editTarget.id), fetchProperty(prop.id)]);
      setAbonos(newAbonos);
      setProp(newProp);
      const freshPay = newProp.payments?.find(p => p.id === editTarget.id);
      if (freshPay) setEditTarget(freshPay);
      setEditingAbono(null);
      toast.success('Abono actualizado');
      onRefresh();
    } catch { toast.error('Error'); }
  };

  const handleDeleteAbono = async (abonoId) => {
    try {
      await deletePaymentAbono(abonoId);
      const [newAbonos, newProp] = await Promise.all([fetchPaymentAbonos(editTarget.id), fetchProperty(prop.id)]);
      setAbonos(newAbonos);
      setProp(newProp);
      const freshPay = newProp.payments?.find(p => p.id === editTarget.id);
      if (freshPay) setEditTarget(freshPay);
      toast.success('Abono eliminado');
      onRefresh();
    } catch { toast.error('Error'); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try { await deletePropertyPayment(deleteTarget.id); toast.success('Pago eliminado'); await reload(); onRefresh(); }
    catch { toast.error('Error'); } finally { setDeleteTarget(null); }
  };

  const handleRevert = async () => {
    if (!editTarget) return;
    setBusy(true);
    try {
      await revertPaymentToProjected(editTarget.id);
      toast.success('Pago revertido a programado');
      setEditTarget(null);
      await reload(); onRefresh();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setBusy(false); }
  };

  const openEdit = async (pp) => {
    setEditTarget(pp);
    setAbonoForm({ date: '', amount: '', notes: '' });
    setEditingAbono(null);
    try { setAbonos(await fetchPaymentAbonos(pp.id)); } catch { setAbonos([]); }
  };

  const payments = prop.payments || [];
  const sorted = [...payments].sort((a, b) => (a.scheduled_date || '').localeCompare(b.scheduled_date || ''));
  const totalPaid = prop.total_paid || 0;
  const totalScheduled = prop.total_scheduled || 0;
  const price = prop.sale_price || 0;
  const saldo = price - totalPaid;
  const pctPaid = price > 0 ? (totalPaid / price) * 100 : 0;
  const pctScheduled = price > 0 ? (totalScheduled / price) * 100 : 0;

  return (
    <Dialog open={true} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#F05A28] to-[#FF6A3D] flex items-center justify-center shadow-sm"><Home className="h-5 w-5 text-white" /></div>
                <div>
                  <p className="text-lg font-semibold">{prop.name || 'Sin comprador'}</p>
                  <p className="text-xs text-slate-400 font-normal">Lote {prop.lot} · Mz {prop.block} · {prop.modelo}</p>
                  {(prop.client_email || prop.client_phone) && (
                    <div className="flex items-center gap-3 mt-1">
                      {prop.client_email && <span className="text-[10px] text-slate-400">{prop.client_email}</span>}
                      {prop.client_phone && <span className="text-[10px] text-slate-400">{prop.client_phone}</span>}
                    </div>
                  )}
                </div>
              </div>
              <Badge variant="outline" className={`${statusColors[prop.status] || ''} text-xs`}>{prop.status}</Badge>
            </DialogTitle>
          </DialogHeader>
        </div>

        {/* Financial Summary */}
        <div className="px-6 pb-4">
          <div className="bg-slate-50 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between text-sm"><span className="text-slate-500">Precio de venta</span><span className="font-semibold tabular-nums">{formatMXN(price)}</span></div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-[9px] uppercase text-slate-400 w-16 shrink-0">Programado</span>
                <div className="flex-1 h-2.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full rounded-full bg-slate-400 transition-all" style={{ width: `${Math.min(pctScheduled, 100)}%` }} /></div>
                <span className="text-[10px] tabular-nums text-slate-500 w-10 text-right">{pctScheduled.toFixed(0)}%</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[9px] uppercase text-[#F05A28] font-medium w-16 shrink-0">Recibido</span>
                <div className="flex-1 h-2.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full rounded-full bg-[#F05A28] transition-all" style={{ width: `${Math.min(pctPaid, 100)}%` }} /></div>
                <span className="text-[10px] tabular-nums text-[#F05A28] font-semibold w-10 text-right">{pctPaid.toFixed(0)}%</span>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div className="text-center"><p className="text-[9px] uppercase text-slate-400">Programado</p><p className="text-xs font-bold tabular-nums">{formatMXN(totalScheduled)}</p></div>
              <div className="text-center"><p className="text-[9px] uppercase text-[#F05A28]">Recibido</p><p className="text-xs font-bold tabular-nums text-[#F05A28]">{formatMXN(totalPaid)}</p></div>
              <div className="text-center"><p className="text-[9px] uppercase text-amber-600">Pendiente</p><p className="text-xs font-bold tabular-nums text-amber-700">{formatMXN(Math.max(saldo, 0))}</p></div>
              <div className="text-center"><p className="text-[9px] uppercase text-slate-400">Liberación</p><p className="text-xs font-bold tabular-nums">{prop.release_amount ? formatMXN(prop.release_amount) : '—'}</p></div>
            </div>
          </div>
        </div>

        {/* Escrituración y Liberación */}
        {(prop.deed_date || prop.delivery_date) && (
          <div className="px-6 pb-4">
            <h3 className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-3">Escrituración, Entrega y Liberación</h3>
            <div className="bg-slate-50 rounded-xl p-4 space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-[9px] uppercase text-slate-400 mb-1">Escrituración Estimada</p>
                  <p className="text-sm tabular-nums font-medium">{prop.deed_date ? formatDate(prop.deed_date) : <span className="text-slate-300">—</span>}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase text-slate-400 mb-1">Entrega Estimada</p>
                  <p className="text-sm tabular-nums font-medium">{prop.delivery_date ? formatDate(prop.delivery_date) : <span className="text-slate-300">—</span>}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase text-[#F05A28] mb-1">Escrituración Real</p>
                  <p className="text-sm tabular-nums font-medium text-[#F05A28]">{prop.actual_deed_date ? formatDate(prop.actual_deed_date) : <span className="text-slate-300">Pendiente</span>}</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <p className="text-[9px] uppercase text-slate-400 mb-1">Liberación Proyectada</p>
                  <p className="text-xs font-bold tabular-nums">{formatMXN(prop.release_amount || 0)}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase text-[#F05A28] mb-1">Liberación Real</p>
                  <p className="text-xs font-bold tabular-nums text-[#F05A28]">{prop.actual_release_amount ? formatMXN(prop.actual_release_amount) : <span className="text-slate-300">—</span>}</p>
                </div>
                <div>
                  <p className="text-[9px] uppercase text-slate-400 mb-1">Estatus</p>
                  <ReleaseBadge status={prop.release_status} />
                </div>
              </div>
              {prop.finiquito && (
                <div className="pt-2 border-t border-slate-200">
                  <p className="text-[9px] uppercase text-slate-400 mb-1">Finiquito Vinculado</p>
                  <div className="flex items-center gap-2 text-xs">
                    <Badge variant="outline" className={`text-[9px] ${prop.finiquito.payment_status === 'received' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-300'}`}>
                      {prop.finiquito.payment_status === 'received' ? 'Recibido' : 'Programado'}
                    </Badge>
                    <span className="tabular-nums font-semibold">{formatMXN(prop.finiquito.scheduled_amount)}</span>
                    <span className="text-slate-400">· {formatDate(prop.finiquito.scheduled_date)}</span>
                  </div>
                </div>
              )}
              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2">
                {!prop.actual_deed_date && (
                  <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={async () => {
                    try { await markPropertyDeeded(prop.id); toast.success('Vivienda marcada como escriturada'); await reload(); onRefresh(); } catch { toast.error('Error'); }
                  }} data-testid="btn-escriturar">Marcar como Escriturada</Button>
                )}
                {prop.release_status !== 'released' && prop.release_amount > 0 && (
                  <ReleaseWithDateButton propertyId={prop.id} onDone={async () => { await reload(); onRefresh(); }} />
                )}
                {prop.release_status === 'released' && (
                  <Button size="sm" variant="ghost" className="h-7 text-[10px] text-red-500 hover:text-red-700" onClick={async () => {
                    if (!confirm('¿Revertir la liberación? La vivienda volverá a estatus proyectado.')) return;
                    try { await revertPropertyRelease(prop.id); toast.success('Liberación revertida'); await reload(); onRefresh(); } catch { toast.error('Error'); }
                  }} data-testid="btn-revertir-liberacion">Revertir Liberación</Button>
                )}
              </div>
            </div>
          </div>
        )}

        <Separator />

        {/* ── Payment Table ── */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Calendario de Pagos</h3>
            <span className="text-xs text-slate-400">{sorted.length} pago{sorted.length !== 1 ? 's' : ''}</span>
          </div>

          {sorted.length === 0 ? (
            <div className="text-center py-8"><Calendar className="h-8 w-8 text-slate-200 mx-auto mb-2" /><p className="text-sm text-slate-400">Sin pagos programados</p></div>
          ) : (
            <div className="overflow-auto">
              <Table data-testid="payments-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2 px-2">Tipo</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2 px-2">Estatus</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2 px-2">F. Programada</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2 px-2 text-right">Monto Prog.</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-[#F05A28] font-semibold py-2 px-2">F. Real</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-[#F05A28] font-semibold py-2 px-2 text-right">Monto Real</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2 px-2 text-right">Dif.</TableHead>
                    <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-2 px-2 w-32">Acción</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map(pp => {
                    const cfg = payTypeConfig[pp.payment_type] || payTypeConfig.intermedio;
                    const diff = pp.payment_status === 'received' && pp.actual_amount != null ? pp.actual_amount - (pp.scheduled_amount || 0) : null;
                    const isReceived = pp.payment_status === 'received';
                    const isPartial = pp.payment_status === 'partial';
                    const isCancelled = pp.payment_status === 'cancelled';
                    return (
                      <TableRow key={pp.id} className={`group ${pp.payment_status === 'overdue' ? 'bg-red-50/40' : isCancelled ? 'opacity-35' : ''}`} data-testid={`pay-row-${pp.id}`}>
                        <TableCell className="py-2.5 px-2">
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className={`${cfg.badge} text-[9px]`}>{cfg.label}</Badge>
                            {pp.aplica_a_credito && <Badge variant="outline" className="text-[7px] bg-blue-50 text-blue-600 border-blue-200">Crédito</Badge>}
                            {pp.fuera_de_flujo && <Badge variant="outline" className="text-[7px] bg-slate-100 text-slate-500 border-slate-200">Fuera flujo</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="py-2.5 px-2"><PayStatusBadge status={pp.payment_status} /></TableCell>
                        <TableCell className="py-2.5 px-2 text-[11px] tabular-nums text-slate-600">{formatDate(pp.scheduled_date)}</TableCell>
                        <TableCell className="py-2.5 px-2 text-right text-[11px] tabular-nums text-slate-600">{formatMXN(pp.scheduled_amount)}</TableCell>
                        <TableCell className="py-2.5 px-2 text-[11px] tabular-nums text-[#F05A28] font-medium">{pp.actual_date ? formatDate(pp.actual_date) : <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className="py-2.5 px-2 text-right text-[11px] tabular-nums font-bold text-[#F05A28]">{pp.actual_amount != null ? formatMXN(pp.actual_amount) : <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className="py-2.5 px-2 text-right">
                          {diff != null ? <span className={`text-[10px] tabular-nums font-semibold ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-red-500' : 'text-slate-400'}`}>{diff === 0 ? '—' : `${diff > 0 ? '+' : ''}${formatMXN(diff)}`}</span> : <span className="text-slate-300 text-[10px]">—</span>}
                        </TableCell>
                        <TableCell className="py-2.5 px-2">
                          <div className="flex items-center gap-1">
                            {!isCancelled && (
                              isReceived ? (
                                <Button size="sm" variant="outline" className="h-6 px-2.5 text-[10px]" onClick={() => openEdit(pp)} data-testid={`pay-edit-${pp.id}`}>
                                  <Pencil className="h-3 w-3 mr-1" /> Editar pago
                                </Button>
                              ) : isPartial ? (
                                <Button size="sm" className="h-6 px-2.5 text-[10px] bg-amber-600 hover:bg-amber-700" onClick={() => openEdit(pp)} data-testid={`pay-add-${pp.id}`}>
                                  <DollarSign className="h-3 w-3 mr-1" /> Abonar
                                </Button>
                              ) : (
                                <>
                                  <Button size="sm" className="h-6 px-2.5 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={() => openEdit(pp)} data-testid={`pay-receive-${pp.id}`}>
                                    <CheckCircle2 className="h-3 w-3 mr-1" /> Registrar pago
                                  </Button>
                                  {canEditPayments && (
                                    <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px] text-slate-400 hover:text-slate-700" onClick={() => setEditSchedTarget(pp)} title="Editar programación">
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                  )}
                                </>
                              )
                            )}
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Eliminar" onClick={() => setDeleteTarget(pp)}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* ── New Payment Form ── */}
          <div className="mt-5 p-4 bg-slate-50/80 rounded-xl border border-dashed border-slate-200">
            <p className="text-xs font-semibold text-slate-600 mb-3 flex items-center gap-1.5"><Plus className="h-3.5 w-3.5 text-[#F05A28]" /> Nuevo Pago</p>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
              <div><label className="text-[10px] text-slate-400 mb-1 block">Tipo</label>
                <Select value={payForm.payment_type} onValueChange={v => setPayForm(f => ({ ...f, payment_type: v }))}><SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger><SelectContent>{PAY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><label className="text-[10px] text-slate-400 mb-1 block">Fecha programada</label><Input type="date" className="h-9 text-xs" value={payForm.scheduled_date} onChange={e => setPayForm(f => ({ ...f, scheduled_date: e.target.value }))} data-testid="detail-pay-date" /></div>
              <div><label className="text-[10px] text-slate-400 mb-1 block">Monto (MXN)</label><Input type="number" className="h-9 text-xs" placeholder="0" value={payForm.scheduled_amount} onChange={e => setPayForm(f => ({ ...f, scheduled_amount: e.target.value }))} data-testid="detail-pay-amount" /></div>
              <div className="flex items-end"><Button className="h-9 w-full" onClick={addPayment} disabled={!payForm.scheduled_date || !payForm.scheduled_amount || busy} data-testid="detail-pay-save"><Plus className="h-3.5 w-3.5 mr-1" /> {alreadyReceived ? 'Registrar' : 'Programar'}</Button></div>
            </div>
            {/* Toggle: already received */}
            <div className="mt-3 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox checked={alreadyReceived} onCheckedChange={v => { setAlreadyReceived(v); if (!v) setPayActual({ actual_date: '', actual_amount: '' }); }} data-testid="toggle-received" />
                <span className="text-[11px] text-slate-600 font-medium">Ya fue cobrado</span>
              </label>
              {alreadyReceived && (
                <div className="grid grid-cols-2 gap-2 pl-6">
                  <div><label className="text-[10px] text-[#F05A28] mb-1 block">Fecha real</label><Input type="date" className="h-8 text-xs" value={payActual.actual_date} onChange={e => setPayActual(f => ({ ...f, actual_date: e.target.value }))} /></div>
                  <div><label className="text-[10px] text-[#F05A28] mb-1 block">Monto real</label><Input type="number" className="h-8 text-xs" placeholder={payForm.scheduled_amount || '0'} value={payActual.actual_amount} onChange={e => setPayActual(f => ({ ...f, actual_amount: e.target.value }))} /></div>
                </div>
              )}
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={payForm.aplica_a_credito || false} onCheckedChange={v => setPayForm(f => ({ ...f, aplica_a_credito: v, fuera_de_flujo: false }))} /><span className="text-[11px] text-slate-500">Aplica directo a crédito</span></label>
                <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={payForm.fuera_de_flujo || false} onCheckedChange={v => setPayForm(f => ({ ...f, fuera_de_flujo: v, aplica_a_credito: false }))} /><span className="text-[11px] text-slate-500">Fuera de flujo (efectivo)</span></label>
              </div>
            </div>
          </div>
        </div>

        {/* ══ UNIFIED EDIT MODAL ══ */}
        {editTarget && (
          <Dialog open={true} onOpenChange={v => { if (!v) { setEditTarget(null); setAbonos([]); setEditingAbono(null); } }}>
            <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-payment-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-[#F05A28]" /> Pago — {(payTypeConfig[editTarget.payment_type] || {}).label || editTarget.payment_type}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-5 py-2">
                {/* Section: Programado — solo lectura */}
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2">Programado</p>
                  <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1.5">
                    <div className="flex justify-between"><span className="text-slate-500">Tipo</span><Badge variant="outline" className={`${(payTypeConfig[editTarget.payment_type] || {}).badge || ''} text-[9px]`}>{(payTypeConfig[editTarget.payment_type] || {}).label || editTarget.payment_type}</Badge></div>
                    <div className="flex justify-between"><span className="text-slate-500">Fecha programada</span><span className="tabular-nums font-medium">{formatDate(editTarget.scheduled_date)}</span></div>
                    <div className="flex justify-between"><span className="text-slate-500">Monto programado</span><span className="tabular-nums font-bold">{formatMXN(editTarget.scheduled_amount)}</span></div>
                    {editTarget.notes && <div className="flex justify-between"><span className="text-slate-500">Notas</span><span className="text-slate-600">{editTarget.notes}</span></div>}
                  </div>
                </div>

                {/* Section: Abonos */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-[#F05A28]">Cobros Realizados</p>
                    {abonos.length > 0 && (() => {
                      const total = abonos.reduce((s, a) => s + (a.amount || 0), 0);
                      const sched = editTarget.scheduled_amount || 0;
                      const pend = sched - total;
                      return (
                        <div className="flex items-center gap-2 text-[10px] tabular-nums">
                          <span className="text-[#F05A28] font-bold">{formatMXN(total)}</span>
                          {pend > 1 && <span className="text-amber-600">/ Pend: {formatMXN(pend)}</span>}
                          {pend <= 0 && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[8px]">Completo</Badge>}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Abonos list */}
                  {abonos.length > 0 ? (
                    <div className="space-y-1.5 mb-3">
                      {abonos.map((a, idx) => (
                        <div key={a.id} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 group">
                          {editingAbono === a.id ? (
                            <EditAbonoRow abono={a} onSave={(updates) => handleSaveAbono(a.id, updates)} onCancel={() => setEditingAbono(null)} />
                          ) : (
                            <>
                              <span className="text-[10px] text-slate-400 w-4">{idx + 1}.</span>
                              <span className="text-[11px] tabular-nums text-slate-600 flex-1">{formatDate(a.date)}</span>
                              <span className="text-[11px] tabular-nums font-bold text-[#F05A28]">{formatMXN(a.amount)}</span>
                              {a.notes && <span className="text-[9px] text-slate-400 truncate max-w-[80px]" title={a.notes}>{a.notes}</span>}
                              <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button variant="ghost" size="icon" className="h-5 w-5 text-slate-300 hover:text-slate-600" onClick={() => setEditingAbono(a.id)}><Pencil className="h-2.5 w-2.5" /></Button>
                                <Button variant="ghost" size="icon" className="h-5 w-5 text-slate-300 hover:text-red-500" onClick={() => handleDeleteAbono(a.id)}><Trash2 className="h-2.5 w-2.5" /></Button>
                              </div>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[10px] text-slate-400 mb-3">Sin cobros registrados.</p>
                  )}

                  {/* Add abono form */}
                  <div className="bg-slate-50/80 rounded-lg p-3 border border-dashed border-slate-200">
                    <p className="text-[10px] font-medium text-slate-500 mb-2"><Plus className="h-3 w-3 inline mr-1 text-[#F05A28]" />Nuevo cobro</p>
                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2">
                      <div><label className="text-[10px] text-[#F05A28] mb-1 block">Fecha</label><Input type="date" className="h-8 text-xs" value={abonoForm.date} onChange={e => setAbonoForm(f => ({ ...f, date: e.target.value }))} data-testid="abono-date" /></div>
                      <div><label className="text-[10px] text-[#F05A28] mb-1 block">Monto</label><Input type="number" step="0.01" className="h-8 text-xs" placeholder="0" value={abonoForm.amount} onChange={e => setAbonoForm(f => ({ ...f, amount: e.target.value }))} data-testid="abono-amount" /></div>
                      <div className="flex items-end"><Button size="sm" className="h-8 px-3 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={handleAddAbono} disabled={busy || !abonoForm.date || !abonoForm.amount} data-testid="abono-save"><Plus className="h-3 w-3" /></Button></div>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                {abonos.length > 0 && (
                  <Button variant="ghost" size="sm" className="text-[11px] text-slate-400 hover:text-red-500 mr-auto" onClick={handleRevert} disabled={busy} data-testid="clear-btn">
                    Limpiar todos los cobros
                  </Button>
                )}
                <Button variant="outline" onClick={() => setEditTarget(null)}>Cerrar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}


        {/* ══ DELETE CONFIRMATION ══ */}
        {deleteTarget && (
          <Dialog open={true} onOpenChange={v => { if (!v) setDeleteTarget(null); }}>
            <DialogContent className="sm:max-w-sm">
              <DialogHeader><DialogTitle>Eliminar pago</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-600 py-2">Se eliminará el pago de <strong>{formatMXN(deleteTarget.scheduled_amount)}</strong> programado para <strong>{formatDate(deleteTarget.scheduled_date)}</strong>.</p>
              <DialogFooter><Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancelar</Button><Button variant="destructive" onClick={confirmDelete} disabled={busy} data-testid="confirm-delete-btn">Eliminar</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* ══ EDIT SCHEDULED MODAL ══ */}
        {editSchedTarget && (
          <EditScheduledModal
            payment={editSchedTarget}
            salePrice={price}
            totalScheduled={totalScheduled}
            onClose={() => setEditSchedTarget(null)}
            onSaved={() => { setEditSchedTarget(null); reload(); onRefresh(); }}
          />
        )}

        {/* ══ EDIT GENERAL DATA ══ */}
        {editGeneralOpen && (
          <EditPropertyModal
            property={prop}
            onClose={() => setEditGeneralOpen(false)}
            onUpdated={(updated) => { setProp(updated); setEditGeneralOpen(false); onRefresh(); }}
            modelos={modelos || []}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── Edit Scheduled Payment Modal ── */
function EditScheduledModal({ payment, salePrice, totalScheduled, onClose, onSaved }) {
  const [form, setForm] = useState({
    payment_type: payment.payment_type,
    scheduled_date: payment.scheduled_date || '',
    scheduled_amount: payment.scheduled_amount || '',
    notes: payment.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isReceived = payment.payment_status === 'received' || payment.payment_status === 'partial';
  const newAmount = parseFloat(form.scheduled_amount) || 0;
  const oldAmount = payment.scheduled_amount || 0;
  const diff = newAmount - oldAmount;
  const newTotal = totalScheduled + diff;
  const totalWarning = salePrice > 0 && Math.abs(newTotal - salePrice) > 1;

  const handleSave = async () => {
    setError('');
    if (newAmount < 0) { setError('El monto no puede ser negativo'); return; }
    if (!form.scheduled_date) { setError('La fecha es requerida'); return; }
    setSaving(true);
    try {
      await updatePropertyPayment(payment.id, {
        payment_type: form.payment_type,
        scheduled_date: form.scheduled_date,
        scheduled_amount: newAmount,
        notes: form.notes || null,
      });
      toast.success('Programación actualizada');
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="edit-scheduled-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-5 w-5 text-[#F05A28]" /> Editar Programación
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {isReceived && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Este pago ya tiene cobros registrados. Modificar la programación puede afectar la diferencia entre lo programado y lo cobrado.</span>
            </div>
          )}

          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Tipo de pago</Label>
            <Select value={form.payment_type} onValueChange={v => setForm(f => ({ ...f, payment_type: v }))}>
              <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{PAY_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Fecha programada</Label>
              <Input type="date" className="h-9 text-xs" value={form.scheduled_date} onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Monto programado</Label>
              <Input type="number" step="0.01" className="h-9 text-xs" value={form.scheduled_amount} onChange={e => setForm(f => ({ ...f, scheduled_amount: e.target.value }))} />
            </div>
          </div>

          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1.5 block">Notas</Label>
            <Input className="h-9 text-xs" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Referencia, folio, motivo del cambio..." />
          </div>

          {totalWarning && (
            <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">Precio de venta</span><span className="tabular-nums font-medium">{formatMXN(salePrice)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Total programado (nuevo)</span><span className={`tabular-nums font-bold ${newTotal > salePrice ? 'text-amber-600' : 'text-slate-700'}`}>{formatMXN(newTotal)}</span></div>
              {Math.abs(newTotal - salePrice) > 1 && (
                <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />El total programado difiere del precio de venta por {formatMXN(Math.abs(newTotal - salePrice))}</p>
              )}
            </div>
          )}

          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="edit-sched-save">{saving ? 'Guardando...' : 'Guardar'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ── Payment Status Badge with icon ── */
function PayStatusBadge({ status }) {
  const config = {
    projected: { className: 'bg-slate-100 text-slate-600 border-slate-300', icon: Clock, label: 'Programado' },
    partial:   { className: 'bg-amber-50 text-amber-700 border-amber-300', icon: Clock, label: 'Parcial' },
    received:  { className: 'bg-emerald-50 text-emerald-700 border-emerald-300', icon: CheckCircle2, label: 'Recibido' },
    overdue:   { className: 'bg-red-50 text-red-600 border-red-300', icon: AlertTriangle, label: 'Vencido' },
    cancelled: { className: 'bg-slate-50 text-slate-400 border-slate-200', icon: Ban, label: 'Cancelado' },
  };
  const c = config[status] || config.projected;
  const Icon = c.icon;
  return (
    <Badge variant="outline" className={`${c.className} text-[9px] gap-1`} data-testid={`badge-${status}`}>
      <Icon className="h-2.5 w-2.5" />
      {c.label}
    </Badge>
  );
}

/* ── Inline edit row for abono ── */
function EditAbonoRow({ abono, onSave, onCancel }) {
  const [form, setForm] = useState({ date: abono.date || '', amount: abono.amount || '', notes: abono.notes || '' });
  return (
    <div className="flex items-center gap-2 flex-1">
      <Input type="date" className="h-7 text-xs flex-1" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
      <Input type="number" step="0.01" className="h-7 text-xs w-24" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
      <Button size="sm" className="h-7 px-2 text-[10px]" onClick={() => onSave({ date: form.date, amount: parseFloat(form.amount) || 0, notes: form.notes || null })}><Check className="h-3 w-3" /></Button>
      <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px]" onClick={onCancel}><XIcon className="h-3 w-3" /></Button>
    </div>
  );
}

function ReleaseBadge({ status }) {
  const config = {
    projected: { cls: 'bg-slate-100 text-slate-600 border-slate-300', label: 'Proyectada' },
    released:  { cls: 'bg-emerald-50 text-emerald-700 border-emerald-300', label: 'Liberada' },
    overdue:   { cls: 'bg-red-50 text-red-600 border-red-300', label: 'Vencida' },
    cancelled: { cls: 'bg-slate-50 text-slate-400 border-slate-200', label: 'Cancelada' },
  };
  const c = config[status] || config.projected;
  return <Badge variant="outline" className={`${c.cls} text-[9px]`}>{c.label}</Badge>;
}

function ReleaseWithDateButton({ propertyId, onDone }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const handleRelease = async () => {
    setSaving(true);
    try {
      await markPropertyReleased(propertyId, { actual_release_date: date });
      toast.success(`Liberación registrada (${formatDate(date)})`);
      setOpen(false);
      if (onDone) await onDone();
    } catch { toast.error('Error al registrar liberación'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Button size="sm" className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={() => setOpen(true)} data-testid="btn-liberar">
        Registrar Liberación
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="text-sm">Registrar Liberación</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs text-slate-600 mb-1.5 block">Fecha de liberación</Label>
              <Input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="release-date-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={handleRelease} disabled={saving || !date} data-testid="confirm-release-btn">
              {saving ? 'Guardando...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


